import React, { useState } from 'react';
import { Typography, Container, Box, Paper, List, ListItem, ListItemText, Divider, Button } from '@mui/material';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { useLanguage } from '../LanguageContext';
import { translations } from '../translations';
import englishInstall from '../assets/install/english_install.pdf';
import frenchInstall from '../assets/install/french_install.pdf';
import chineseInstall from '../assets/install/chinese_install.pdf';
import PdfViewerModal from '../components/PdfViewerModal';

const InstallationView = () => {
    const { language } = useLanguage();
    const t = translations[language].installation;
    const [modalOpen, setModalOpen] = useState(false);

    // Select the appropriate PDF based on language
    const getInstallPdf = () => {
        switch (language) {
            case 'fr':
                return frenchInstall;
            case 'zh':
                return chineseInstall;
            default:
                return englishInstall;
        }
    };

    const handleOpenPdf = () => {
        setModalOpen(true);
    };

    const handleCloseModal = () => {
        setModalOpen(false);
    };

    return (
        <Box sx={{ bgcolor: '#f5f5f5', minHeight: 'calc(100vh - 100px)', py: 6, px: 2 }}>
            <Container maxWidth="md" sx={{ px: { xs: 0, sm: 2 } }}>
                <Typography
                    variant="h3"
                    component="h1"
                    gutterBottom
                    align="center"
                    sx={{ fontWeight: 600, mb: 2, fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' } }}
                >
                    {t.title}
                </Typography>
                <Typography
                    variant="h6"
                    align="center"
                    color="text.secondary"
                    sx={{ mb: 6, fontSize: { xs: '1rem', sm: '1.25rem' } }}
                >
                    {t.subtitle}
                </Typography>

                {/* PDF Download Section */}
                <Paper
                    elevation={3}
                    sx={{
                        p: 3,
                        mb: 4,
                        textAlign: 'center',
                        background: 'linear-gradient(135deg, #1565C0 0%, #1976D2 100%)',
                        color: 'white'
                    }}
                >
                    <PictureAsPdfIcon sx={{ fontSize: 40, mb: 1, opacity: 0.9 }} />
                    <Typography variant="h6" gutterBottom fontWeight={600}>
                        {t.pdfTitle}
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 2, opacity: 0.9 }}>
                        {t.pdfDescription}
                    </Typography>
                    <Button
                        variant="contained"
                        size="medium"
                        onClick={handleOpenPdf}
                        startIcon={<PictureAsPdfIcon />}
                        sx={{
                            px: 3,
                            py: 1,
                            fontSize: '0.95rem',
                            fontWeight: 600,
                            bgcolor: 'white',
                            color: 'primary.main',
                            '&:hover': {
                                bgcolor: 'rgba(255,255,255,0.9)',
                                transform: 'scale(1.05)',
                            },
                            transition: 'all 0.2s'
                        }}
                    >
                        {t.download}
                    </Button>
                </Paper>
                <Paper elevation={3} sx={{ py: 0, px: 4, mb: 4 }}>
                    <List>
                        {t.steps.map((step, index) => (
                            <React.Fragment key={index}>
                                <ListItem
                                    alignItems="flex-start"
                                    sx={{
                                        py: { xs: 1},
                                        px: { xs: 0, sm: 2 },
                                        flexDirection: 'row'
                                    }}
                                >
                                    <Box
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'flex-start',
                                            mb: 0,
                                            mr: { xs: 1.5, sm: 3 },
                                            pt: 0.5
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                width: { xs: 28, sm: 36 },
                                                height: { xs: 28, sm: 36 },
                                                minWidth: { xs: 28, sm: 36 },
                                                minHeight: { xs: 28, sm: 36 },
                                                borderRadius: '50%',
                                                bgcolor: 'primary.main',
                                                color: 'white',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                fontWeight: 700,
                                                fontSize: { xs: '0.9rem', sm: '1rem' },
                                                flexShrink: 0
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontWeight: 700,
                                                    fontSize: { xs: '0.9rem', sm: '1rem' },
                                                    lineHeight: 1
                                                }}
                                            >
                                                {index + 1}
                                            </Typography>
                                        </Box>
                                    </Box>
                                    <ListItemText
                                        primary={
                                            <Typography variant="h6" fontWeight={600} gutterBottom sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                                {step.title}
                                            </Typography>
                                        }
                                        secondary={
                                            <Typography variant="body1" color="text.secondary" sx={{ fontSize: { xs: '0.9rem', sm: '1rem' } }}>
                                                {step.description}
                                            </Typography>
                                        }
                                    />
                                </ListItem>
                                {index < t.steps.length - 1 && <Divider />}
                            </React.Fragment>
                        ))}
                    </List>
                </Paper>

                <Paper
                    elevation={2}
                    sx={{
                        p: 3,
                        bgcolor: 'info.light',
                        border: '2px solid',
                        borderColor: 'info.main'
                    }}
                >
                    <Typography variant="h6" gutterBottom fontWeight={600} sx={{ textAlign: 'left' }}>
                        💡 {t.proTips}
                    </Typography>
                    {t.tips.map((tip, index) => (
                        <Typography key={index} variant="body2" paragraph={index < t.tips.length - 1} sx={{ textAlign: 'left' }}>
                            • {tip}
                        </Typography>
                    ))}
                </Paper>
            </Container>

            {/* PDF Modal */}
            <PdfViewerModal
                open={modalOpen}
                onClose={handleCloseModal}
                pdfUrl={getInstallPdf()}
                title={t.pdfTitle}
            />
        </Box>
    );
};

export default InstallationView;